import { useDictionary } from '../../../../../../utilities/hooks/UseDictionary';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useAccount } from '../../../../../../utilities/hooks/UseAccount';
import {AmountFormatHelper} from "../../../../../../utilities/helpers/AmountFormatHelper";
import Tooltip from '../../../../common/Tooltip';
import CustomCheckbox from '../../../../common/CustomCheckbox';
import PremiumBadge from '../../../../common/PremiumBadge';
import { useSubscription } from '../../../../../../utilities/hooks/useSubscription';

const Reselling = ({
	setEnabled,
	isReselling,
	setAllowReselling,
	resellerCommission,
	setResellerCommission,
	upiriaCommission,
	includeInMarketplace,
	setIncludeInMarketplace,
	includeInWebSite,
	setIncludeInWebSite,
	allowOffline,
	setAllowOffline
}) => {
	const dictionary = useDictionary();
	const account = useAccount();
	const isVerified = account?.verification_status?.value === '3';
	const isSubscription = useSubscription();
	const subscriptionTooltip = !isSubscription
		? dictionary.upgrade_subscription_channels
		: null;

	const onRadioChanged = (e) => {
		setEnabled(true);
		if (e.target.value === 'hide') {
			setAllowReselling(false);
		} else {
			setAllowReselling(true);
		}
	};

	const onChange = (e) => {
		setResellerCommission(e.target.value * 100);
	};

	useEffect(() => {
		setEnabled(true);
	}, []);

	return (
		<div className="new-experience-form-wrapper">
			<div className="new-experience-form-container thin-scrollbar">
				<h2 style={{ fontSize: '24px' }}>{dictionary.sales_channels}</h2>
				<div style={{ marginTop: '30px', opacity: 0.8 }} className="text">
					{dictionary.sales_channels_description}
				</div>
				<h3 style={{ fontSize: '16px', marginTop: '40px' }}>
					{dictionary.reselling}
				</h3>
				<div style={{ marginTop: '30px', opacity: 0.8 }} className="text">
					{dictionary.reselling_your_experience}
				</div>
				<div className="custom-policy-radio-button reselling-radio-button">
					<label style={{ marginTop: '32px' }} className={`radio-label ${!isVerified && 'radio-disabled'}`}>
						{dictionary.enable_reselling}
						<input
							onChange={onRadioChanged}
							checked={isReselling}
							value="allow"
							type="radio"
							disabled={!isVerified}
							name="allowReselling"
							data-testid="enable-reselling-radio"
						/>
						<span className="checkmark" />
						{!isVerified && (
							<Tooltip content={dictionary.you_need_to_be_verified_account} />
						)}
					</label>
				</div>
				<label className="radio-label">
					{dictionary.disable_reselling}
					<input
						onChange={onRadioChanged}
						checked={isReselling !== undefined && !isReselling}
						value="hide"
						type="radio"
						disabled={!isVerified}
						name="allowReselling"
						data-testid="disable-reselling-radio"
					/>
					<span className="checkmark" />
				</label>
				<div style={{ display: isReselling ? 'block' : 'none' }}>
					<h3 style={{ fontSize: '16px', marginTop: '40px' }}>
						{dictionary.commissions}
					</h3>
					<div
						className="group-inputs d-flex"
						style={{
							marginTop: 16,
							alignItems: 'center',
							justifyContent: 'space-between',
							marginBottom: 16
						}}
					>
						<h4 style={{ fontWeight: 400 }}>
							{dictionary.resellers_commission}
						</h4>
						<div className="percent-input-container">
							<input
								type="number"
								min={1}
								max={50}
								value={resellerCommission / 100}
								onChange={onChange}
								disabled={!isVerified}
								className="percent-input"
								data-testid="reseller-commission-input"
							/>
						</div>
					</div>
					<div
						className="group-inputs d-flex"
						style={{
							marginTop: '0',
							alignItems: 'center',
							justifyContent: 'space-between',
							marginBottom: '0'
						}}
					>
						<h4 style={{ fontWeight: 400 }}>{dictionary.upiria_commission}</h4>
						<div className="percent-input-container">
							<input
								disabled
								value={AmountFormatHelper.formatAmount(upiriaCommission)}
								type="number"
								className="percent-input"
								data-testid="upiria-commission"
							/>
						</div>
					</div>
					<div
						style={{ marginTop: '16px', opacity: 0.6 }}
						className="terms-text"
					>
						{dictionary.resell_an_experience}
					</div>
				</div>
				{isVerified && (
					<>
						<div
							className="d-flex items-center"
							style={{ marginTop: '40px', gap: '12px' }}
						>
							<h3 style={{ fontSize: '16px' }}>
								{dictionary.direct_channels}
							</h3>
							{!isSubscription ? <PremiumBadge inline /> : null}
						</div>
						<div style={{ marginTop: '24px' }}>
							<CustomCheckbox
								label={dictionary.include_in_marketplace}
								checked={includeInMarketplace}
								setChecked={setIncludeInMarketplace}
								name="include_in_marketplace"
								disabled={!isSubscription}
								tooltip={subscriptionTooltip}
							/>
						</div>
						<div style={{ marginTop: '16px' }}>
							<CustomCheckbox
								label={dictionary.include_in_provider_website}
								checked={includeInWebSite}
								setChecked={setIncludeInWebSite}
								name="include_in_web_site"
								disabled={!isSubscription}
								tooltip={subscriptionTooltip}
							/>
						</div>
						<div style={{ marginTop: '16px' }}>
							<CustomCheckbox
								label={dictionary.allow_offline_bookings}
								checked={allowOffline}
								setChecked={setAllowOffline}
								name="allow_offline"
								disabled={!isSubscription}
								tooltip={subscriptionTooltip}
							/>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

Reselling.propTypes = {
	setEnabled: PropTypes.func,
	isReselling: PropTypes.bool,
	setAllowReselling: PropTypes.func,
	resellerCommission: PropTypes.number,
	setResellerCommission: PropTypes.func,
	upiriaCommission: PropTypes.number,
	includeInMarketplace: PropTypes.bool,
	setIncludeInMarketplace: PropTypes.func,
	includeInWebSite: PropTypes.bool,
	setIncludeInWebSite: PropTypes.func,
	allowOffline: PropTypes.bool,
	setAllowOffline: PropTypes.func
};

export default Reselling;
